import { MODULES, isStaffSurface, personaOf, type ModuleKey, type NavGroup } from '@titan/shared';
import { SHIPPED_MODULES } from '@titan/modules';
import { Bot, Calculator, LayoutDashboard, Route, Settings2, ShieldCheck, Ticket, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router';
import { useSession } from '../session/SessionProvider.js';
import { ScreenBody } from '../shell/Shell.js';
import { Avatar, Card } from '../ui/index.js';
import styles from './Welcome.module.css';

const MODULE_ICON: Record<ModuleKey, LucideIcon> = {
  dash: LayoutDashboard,
  tick: Ticket,
  ai: Bot,
  mod: Route,
  roi: Calculator,
  sec: ShieldCheck,
};

/** The first screen of a nav group is where its launcher card lands. */
function landing(nav: NavGroup[], key: NavGroup['key']): string | null {
  return nav.find((g) => g.key === key)?.path ?? null;
}

interface LauncherCard {
  key: string;
  title: string;
  body: string;
  to: string;
  Icon: LucideIcon;
}

/**
 * Spec 01 Welcome: one card per entitled module. Under impersonation, the impersonated
 * tenant's entitlements. Never a disabled or teaser card.
 */
export function Welcome() {
  const { me } = useSession();
  if (!me) return null;
  const persona = personaOf(me.actor);
  const staff = isStaffSurface(me.actor);
  const entitlements = persona.kind === 'tenant' ? persona.tenant.entitlements : null;

  const cards: LauncherCard[] = [];
  const opsPath = landing(me.nav, 'ops');
  if (opsPath && SHIPPED_MODULES.includes('dash') && (staff || entitlements?.dash)) {
    cards.push({ key: 'dash', title: MODULES.dash.label, body: MODULES.dash.description, to: opsPath, Icon: MODULE_ICON.dash });
  }
  const secPath = me.nav.find((g) => g.key === 'ops')?.items.find((i) => i.key === 'sec')?.path;
  if (secPath && SHIPPED_MODULES.includes('sec') && (staff || entitlements?.sec)) {
    cards.push({ key: 'sec', title: MODULES.sec.label, body: MODULES.sec.description, to: secPath, Icon: MODULE_ICON.sec });
  }
  for (const key of ['tick', 'ai', 'mod', 'roi'] as const) {
    const path = landing(me.nav, key);
    if (path && SHIPPED_MODULES.includes(key) && (staff || entitlements?.[key])) {
      cards.push({ key, title: MODULES[key].label, body: MODULES[key].description, to: path, Icon: MODULE_ICON[key] });
    }
  }
  const adminPath = landing(me.nav, 'admin');
  if (adminPath) {
    cards.push({
      key: 'admin',
      title: staff ? 'Administration' : 'Your organization',
      body: staff ? 'Tenants, entitlements, the DinoCloud team and every tenant’s roles.' : 'The people at your organization, their groups and roles.',
      to: adminPath,
      Icon: Settings2,
    });
  }

  const roleLine =
    persona.kind === 'staff'
      ? persona.role === 'superadmin'
        ? 'Platform Owner · DinoCloud'
        : 'DinoCloud staff'
      : `${persona.isSuperadmin ? 'Superadmin' : 'Member'} at ${persona.tenant.name}`;

  return (
    <>
      <div className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroTop}>
            <div className={styles.wordmark}>
              <span className={styles.wordmarkTitle}>Titan</span>
              <span className={styles.wordmarkSub}>by DinoCloud</span>
            </div>
            <div className={styles.persona}>
              <div className={styles.personaText}>
                <span className={styles.personaName}>{persona.name}</span>
                <span className={styles.personaRole}>{roleLine}</span>
              </div>
              <Avatar name={persona.name} variant="dark" />
            </div>
          </div>
          <div>
            <div className={styles.eyebrow}>first sign-in</div>
            <h1 className={styles.title}>Welcome to Titan</h1>
            <p className={styles.body}>
              This is where your cloud operation lives: infrastructure and application health, modernization work, cost and ROI, and the AI
              agents that run alongside your team. Everything below is already connected to your organization.
            </p>
          </div>
        </div>
      </div>
      <ScreenBody>
        <div className={styles.grid}>
          {cards.map(({ key, title, body, to, Icon }) => (
            <Card key={key} as="article" clickable>
              <Link to={to} className={styles.card}>
                <Icon size={19} strokeWidth={1.75} className={styles.cardIcon} aria-hidden="true" />
                <span className={styles.cardTitle}>{title}</span>
                <span className={styles.cardBody}>{body}</span>
                <span className={styles.cardFoot}>Open</span>
              </Link>
            </Card>
          ))}
        </div>
      </ScreenBody>
    </>
  );
}
